/**
 * Where a Paya ID sign-in lands (#1625).
 *
 * The session is minted at the apex, where the tenant is known, and the browser
 * arrives here on the tenant's own host carrying a single-use id in the URL
 * fragment. A fragment rather than a query string: it never leaves the browser,
 * so the id stays out of the edge proxy's access log.
 *
 * The id is spent on first use, so this runs the exchange exactly once — a
 * second attempt after a React strict-mode double render or a refresh would
 * find nothing and show a failure for a sign-in that actually succeeded.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useOidcHandoff } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import {
  UNLOCK_POPUP_MESSAGE_TYPE,
  captureUnlockPopupClassification,
  clearUnlockPopupSession,
  hasSameOriginOpener,
  postUnlockPopupResultToOpener,
  requestUnlockPopupChallenge,
  type UnlockPopupChallengeMessage,
} from '@/components/auth/unlockPopup';

/** `#id=<token>` — the only thing the apex puts in front of the browser. */
function handoffIdFromFragment(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const id = params.get('id');
  return id !== null && id !== '' ? id : null;
}

type HandoffUser = { id: string; tenantId: string };

function deliverUnlockPopupResult(
  data: { accessToken: string; refreshToken: string; user: HandoffUser },
  challenge: UnlockPopupChallengeMessage | null,
  onUndelivered: () => void,
): void {
  if (challenge === null) {
    postUnlockPopupResultToOpener({
      type: UNLOCK_POPUP_MESSAGE_TYPE,
      status: 'error',
    });
    clearUnlockPopupSession();
    onUndelivered();
    return;
  }
  if (data.user.id !== challenge.userId || data.user.tenantId !== challenge.tenantId) {
    postUnlockPopupResultToOpener({
      type: UNLOCK_POPUP_MESSAGE_TYPE,
      status: 'error',
    });
    clearUnlockPopupSession();
    onUndelivered();
    return;
  }
  postUnlockPopupResultToOpener({
    type: UNLOCK_POPUP_MESSAGE_TYPE,
    status: 'success',
    nonce: challenge.nonce,
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    sessionFamilyId: challenge.sessionFamilyId,
    lockGeneration: challenge.lockGeneration,
    userId: challenge.userId,
    tenantId: challenge.tenantId,
    sessionEpoch: challenge.sessionEpoch,
  });
  clearUnlockPopupSession();
  window.close();
}

export default function HandoffPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handoff = useOidcHandoff();
  const started = useRef(false);
  const [missingId, setMissingId] = useState(false);
  const [unlockDeliveryFailed, setUnlockDeliveryFailed] = useState(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const id = handoffIdFromFragment(window.location.hash);
    if (id === null) {
      setMissingId(true);
      return;
    }
    // Drop the id from the address bar before the exchange resolves, so a
    // shared screenshot or a copied URL carries nothing spendable.
    window.history.replaceState(null, '', window.location.pathname);

    const exchange = (challenge: UnlockPopupChallengeMessage | null) => {
      const unlockFlow = captureUnlockPopupClassification(challenge);
      handoff.mutate(id, {
        onSuccess: (data) => {
          if (unlockFlow) {
            deliverUnlockPopupResult(data, challenge, () => setUnlockDeliveryFailed(true));
            return;
          }
          const target = useAuthStore.getState().consumeRedirectAfterLogin() ?? '/';
          void navigate(target, { replace: true });
        },
        onError: () => {
          if (unlockFlow) {
            postUnlockPopupResultToOpener({
              type: UNLOCK_POPUP_MESSAGE_TYPE,
              status: 'error',
            });
            clearUnlockPopupSession();
          }
        },
      });
    };

    // Ordinary sign-in has no opener: spend the id in this turn. An unlock
    // popup asks the opener for identity first so this store's epoch (always
    // 0 in a freshly loaded window) is never sent as the lock identity. An
    // unmarked same-origin opener that never answers is ordinary sign-in.
    if (hasSameOriginOpener()) {
      void requestUnlockPopupChallenge().then(exchange);
      return;
    }
    exchange(null);
    // The ref guard is what keeps the single-use id from being spent twice
    // (Strict Mode, mutation identity). handoff and navigate are listed so
    // the exchange closes over the current functions; re-runs no-op via the ref.
  }, [handoff, navigate]);

  // Landing here without an id means the link was opened by hand or the
  // fragment was stripped; a failed exchange means it was spent, expired or
  // never real. The person can do the same thing about either.
  const failed = missingId || handoff.isError || unlockDeliveryFailed;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Stack spacing={3} alignItems="center" sx={{ maxWidth: 420, textAlign: 'center' }}>
        {handoff.isPending && (
          <>
            <CircularProgress />
            <Typography variant="body1">{t('handoff.signingIn')}</Typography>
          </>
        )}
        {failed && (
          <>
            <Typography variant="h6">{t('handoff.failedTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('handoff.failedBody')}
            </Typography>
            <Button variant="contained" onClick={() => void navigate('/login', { replace: true })}>
              {t('handoff.backToLogin')}
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
